import React, { useState } from 'react';
import './PlayersPanel.css';
import { updatePlayer, deletePlayer, saveTonightSession } from '../services/api';
import AddPlayerForm from './AddPlayerForm';

function PlayersPanel({ players, tonightPlayerIds, onPlayersUpdate, onSessionUpdate }) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Ensure tonightPlayerIds is always an array
  const safeTonightPlayerIds = Array.isArray(tonightPlayerIds) ? tonightPlayerIds : [];

  // Handle adding a player - does NOT auto-select for tonight
  const handlePlayerAdded = async () => {
    await onPlayersUpdate();
  };

  // Handle toggling a player for tonight
  const handleToggleTonight = async (playerId) => {
    console.log('handleToggleTonight called for player:', playerId);
    console.log('Current safeTonightPlayerIds:', safeTonightPlayerIds);
    console.log('saving state:', saving);
    
    if (saving) {
      console.log('Already saving, ignoring click');
      return;
    }
    
    const isSelected = safeTonightPlayerIds.includes(playerId);
    const newTonightIds = isSelected
      ? safeTonightPlayerIds.filter(id => id !== playerId)
      : [...safeTonightPlayerIds, playerId];

    console.log('New tonightPlayerIds to save:', newTonightIds);

    setSaving(true);
    try {
      console.log('Calling saveTonightSession with:', newTonightIds);
      const result = await saveTonightSession(newTonightIds);
      console.log('Save result:', result);
      console.log('Calling onSessionUpdate');
      await onSessionUpdate();
      console.log('onSessionUpdate completed');
    } catch (err) {
      console.error('Error updating tonight selection:', err);
      console.error('Error details:', err.message, err.stack);
      await onSessionUpdate();
    } finally {
      setSaving(false);
      console.log('Saving state set to false');
    }
  };

  // Handle editing a player
  const handleEditClick = (player) => {
    setEditingId(player.id);
    setEditName(player.name);
    setError(null);
  };

  const handleEditSubmit = async (e, playerId) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      setError('Player name is required');
      return;
    }

    // Check for duplicate name (case-insensitive) on frontend
    const normalizedName = editName.trim().toLowerCase();
    const isDuplicate = players.some(p => 
      p.name.toLowerCase() === normalizedName && p.id !== playerId
    );
    if (isDuplicate) {
      setError(`Player name "${editName.trim()}" already exists`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updatePlayer(playerId, editName.trim());
      setEditingId(null);
      setEditName('');
      await onPlayersUpdate();
    } catch (err) {
      if (err.message.includes('already exists') || err.message.includes('409')) {
        setError(`Player name "${editName.trim()}" already exists`);
      } else {
        setError(err.message || 'Failed to update player');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setError(null);
  };

  // Handle deleting a player
  const handleDelete = async (playerId) => {
    if (!window.confirm('Are you sure you want to delete this player?')) {
      return;
    }

    setSaving(true);
    try {
      await deletePlayer(playerId);
      // Player deletion also removes from tonightPlayerIds on backend
      await onPlayersUpdate();
      await onSessionUpdate();
    } catch (err) {
      console.error('Error deleting player:', err);
      await onPlayersUpdate();
      await onSessionUpdate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="players-panel">
      <div className="panel-header">
        <h2>Players</h2>
      </div>

      {error && <div className="panel-error">{error}</div>}

      <AddPlayerForm onPlayerAdded={handlePlayerAdded} players={players} />

      {players.length === 0 ? (
        <div className="empty-message">
          <p>No players yet. Add your first player above!</p>
        </div>
      ) : (
        <div className="players-list">
          {players.map((player) => {
            const isSelectedForTonight = safeTonightPlayerIds.includes(player.id);
            const isEditing = editingId === player.id;

            return (
              <div key={player.id} className="player-row">
                {isEditing ? (
                  <form 
                    className="player-edit-form" 
                    onSubmit={(e) => handleEditSubmit(e, player.id)}
                  >
                    <input
                      type="text"
                      className="edit-input"
                      value={editName}
                      onChange={(e) => {
                        setEditName(e.target.value);
                        if (error) setError(null);
                      }}
                      autoFocus
                      disabled={saving}
                    />
                    <div className="edit-actions">
                      <button 
                        type="submit" 
                        className="btn btn-small btn-save"
                        disabled={saving || !editName.trim()}
                      >
                        ✓
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-small btn-cancel"
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        ✕
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <span className="player-name">{player.name}</span>
                    <div className="player-actions">
                      <button
                        className={`btn btn-small ${isSelectedForTonight ? 'btn-selected' : 'btn-add-tonight'}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('=== BUTTON CLICKED ===');
                          console.log('Player ID:', player.id);
                          console.log('Saving state:', saving);
                          console.log('Is selected:', isSelectedForTonight);
                          if (!saving) {
                            handleToggleTonight(player.id);
                          } else {
                            console.log('Button is disabled (saving)');
                          }
                        }}
                        disabled={saving}
                        title={isSelectedForTonight ? 'Remove from Tonight' : 'Add to Tonight'}
                        type="button"
                        style={{ 
                          cursor: saving ? 'not-allowed' : 'pointer',
                          userSelect: 'none',
                          pointerEvents: saving ? 'none' : 'auto'
                        }}
                      >
                        {saving ? '...' : (isSelectedForTonight ? '✓ Selected' : '+ Tonight')}
                      </button>
                      <button
                        className="btn btn-small btn-edit"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditClick(player);
                        }}
                        disabled={saving}
                        title="Edit"
                        type="button"
                      >
                        ✎
                      </button>
                      <button
                        className="btn btn-small btn-delete"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(player.id);
                        }}
                        disabled={saving}
                        title="Delete"
                        type="button"
                      >
                        ✕
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {saving && <div className="saving-indicator">Saving...</div>}
    </div>
  );
}

export default PlayersPanel;
